import { Quote } from "../types/quote";
import { Allowance, Allowances } from "../store/global/getAllowances";
import { Chain } from "@rainbow-me/rainbowkit";
import { Amount } from "../types/amount";
import { mainnet, polygon } from "viem/chains";
import { Token } from "../store/global/TokenEnum";
import { TokenList } from "../store/global/getAllTokenLists";
import { OMG_CONTRACTS, REVOKE_BEFORE_APPROVAL_REQUIRED, USDT_CONTRACTS } from "../config/consts";
import { decodeEventLog, TransactionReceipt } from "viem";
import { customERC20ABI } from "./abis/customERC20Abi";
import { nonStandardERC20Abi } from "./abis/nonStandardERC20Abi";
import { erc20ABI } from "wagmi";
import { getTokenDecimals } from "./tokenUtils";

export const tokensOut = (quote: Quote | undefined) => {
  if (quote) {
    return Object.keys(quote?.takerTokens);
  }
  return [];
};

/*
  Only show the revoke required text when allowance is not 0 and contract prevents increasing allowances
  TODO: could this be done by simulating instead of a static list?
  TODO 2: Should this also include network?
*/
export const shouldRevokeFirst = (token: Token, allowances: Allowances): boolean => {
  const tokenAllowance = allowances.find(entry => entry.symbol === token);
  // Check exceptions
  if (REVOKE_BEFORE_APPROVAL_REQUIRED.includes(token) && tokenAllowance?.allowance.value.gt(0)) {
    return true;
  }
  return false;
};

export const getApprovalAmountFromReceipt = (
  r: TransactionReceipt,
  token: Token,
  tokenList: TokenList | undefined
): Amount => {
  const decodedEventLog = decodeEventLog({
    abi: erc20ABI,
    data: r.logs[0].data,
    topics: r.logs[0].topics,
  });
  if (decodedEventLog?.args.value !== undefined) {
    const decimals = getTokenDecimals(token, tokenList);
    const reApprovedAmount = new Amount(decodedEventLog.args.value.toString()).div(
      new Amount((10 ** decimals).toString())
    );
    console.log(`Reapproved for ${reApprovedAmount.toString()} ${token}`);
    return reApprovedAmount;
  }
  console.error(`Could not get approval amount from receipt: ${JSON.stringify(r)}`);
  return Amount.ZERO;
};

export const getAmountNeededFromQuote = (quote: Quote | undefined, symbol: Token): Amount => {
  const tradeAmount = quote?.takerTokens[symbol]?.amount ?? Amount.ZERO;
  return tradeAmount;
};

export const getTokensToApprove = (
  quote: Quote | undefined,
  allowances: Allowances
): Array<Allowance & { needed: Amount }> => {
  const tokens = tokensOut(quote);
  return allowances
    .filter(entry => {
      const tradeAmount = getAmountNeededFromQuote(quote, entry.symbol).value;
      const isTradeLargerThanAllowance = tradeAmount.gt(entry.allowance.value);
      const shouldApprove =
        tokens.includes(entry.symbol) &&
        (entry.allowance.value.isZero() || isTradeLargerThanAllowance);
      return shouldApprove;
    })
    .map(entry => {
      const needed = getAmountNeededFromQuote(quote, entry.symbol);
      return {
        ...entry,
        needed,
      };
    });
};

interface PolgyonGasStationResponse {
  safeLow: {
    maxPriorityFee: number;
    maxFee: number;
  };
  standard: {
    maxPriorityFee: number;
    maxFee: number;
  };
  fast: {
    maxPriorityFee: number;
    maxFee: number;
  };
  estimatedBaseFee: number;
  blockTime: number;
  blockNumber: number;
}

const POLYGON_GAS_STATION_URL = "https://gasstation.polygon.technology/v2";

export interface GasEstimate {
  maxFeePerGas: bigint;
  maxPriorityFeePerGas: bigint;
}

export const getPolygonPublicGasEstimate = (network: Chain): Promise<GasEstimate | undefined> => {
  if (network.id === polygon.id) {
    return fetch(POLYGON_GAS_STATION_URL)
      .then(res => {
        if (res.ok) {
          return res.json();
        }
      })
      .then((json: PolgyonGasStationResponse) => {
        const toWei = 10 ** 9;
        const maxFee = json.fast.maxFee;
        const maxPriorityFee = json.fast.maxPriorityFee;
        return {
          maxFeePerGas: BigInt(Math.round(maxFee) * toWei),
          maxPriorityFeePerGas: BigInt(Math.round(maxPriorityFee) * toWei),
        };
      });
  }
  return Promise.resolve(undefined);
};

export const getAbiToUse = (network: Chain, contract: `0x${string}`) => {
  const isUSDTonEthereum = network.id === mainnet.id && contract === USDT_CONTRACTS[network.id];
  const isOMGonEthereum = network.id === mainnet.id && contract === OMG_CONTRACTS[network.id];
  const abiToUse = isUSDTonEthereum || isOMGonEthereum ? nonStandardERC20Abi : customERC20ABI;
  return abiToUse;
};
